import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { client, sessionSink, unwrap } from "./client";
import { ApiError } from "./errors";
import type { LoginOut, MfaSessionState, Session } from "./types";

/**
 * §A2.2/§B1.1: `{operator, expires_at, csrf_token}` lives in memory, in this context, and nowhere else. It is
 * populated from the body of `POST /api/v1/session`, recovered from `GET /api/v1/session` on mount (which is what
 * lets a page reload keep working), and cleared on any 401.
 */
export type SessionState = {
  session: Session | null;
  status: "loading" | "authenticated" | "anonymous";
  /**
   * xmg-sec-mfa (S3a): where a login-issued session stands on its second factor. `"verified"` is fully signed in;
   * `"required"` / `"enrollment_required"` mean the password was accepted but every route except the MFA ones
   * answers 403 until a code is passed (or a factor set up). `null` for an operator-key session and while
   * anonymous. `RequireSession` reads it to route a half-signed-in user to `/auth/mfa` or `/auth/mfa/setup`.
   */
  mfa: MfaSessionState | null;
  /** Called by the MFA screens once the server accepted a code: the session is now fully signed in. */
  markMfaVerified: () => void;
  login: (apiKey: string) => Promise<void>;
  /** P1a: `POST /api/v1/auth/login` — the multiuser email/password exchange. It sets the SAME cookie
   * `create_session` does (§A2.2's operator/expires_at/csrf_token shape), just under `email` rather than
   * `operator`, so `apply()` below is the only place that reshapes it. */
  loginWithPassword: (email: string, password: string) => Promise<MfaSessionState>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<SessionState["status"]>("loading");
  const mounted = useRef(true);

  const apply = useCallback((next: Session | null) => {
    setSession(next);
    setStatus(next === null ? "anonymous" : "authenticated");
    sessionSink.token = next === null ? null : next.csrf_token;
  }, []);

  const refresh = useCallback(async () => {
    try {
      const next = await unwrap(client.GET("/api/v1/session", {}));
      if (mounted.current) apply(next as Session);
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) {
        // A network failure is not a logout: keep whatever we hold and let the caller's query surface the error.
        if (mounted.current) setStatus((prev) => (prev === "loading" ? "anonymous" : prev));
        return;
      }
      if (mounted.current) apply(null);
    }
  }, [apply]);

  const login = useCallback(
    async (apiKey: string) => {
      const next = await unwrap(client.POST("/api/v1/session", { body: { api_key: apiKey } }));
      apply(next as Session);
    },
    [apply],
  );

  const loginWithPassword = useCallback(
    async (email: string, password: string): Promise<MfaSessionState> => {
      const next = await unwrap(client.POST("/api/v1/auth/login", { body: { email, password } }));
      const loginOut = next as LoginOut;
      apply({
        operator: loginOut.email,
        expires_at: loginOut.expires_at,
        csrf_token: loginOut.csrf_token,
        mfa: loginOut.mfa,
      });
      return loginOut.mfa;
    },
    [apply],
  );

  const markMfaVerified = useCallback(() => {
    // xmg-sec-mfa-takeover: the first pass rotated the session; `api/mfa.ts` already put its new CSRF token
    // on `sessionSink`, and the in-memory session follows it.
    setSession((prev) =>
      prev === null ? prev : { ...prev, mfa: "verified", csrf_token: sessionSink.token ?? prev.csrf_token },
    );
  }, []);

  const logout = useCallback(async () => {
    try {
      // P1a: `auth_logout` revokes the `UserSession` row a login cookie carries a `sid` for, and is a no-op
      // beyond clearing the cookie for a legacy operator-key session (no `sid` to revoke) — one endpoint
      // covers both, so the header's "Sign out" never has to know which kind of session it is looking at.
      await unwrap(client.POST("/api/v1/auth/logout", {}));
    } finally {
      apply(null);
    }
  }, [apply]);

  useEffect(() => {
    mounted.current = true;
    sessionSink.onUnauthorized = () => {
      if (mounted.current) apply(null);
    };
    sessionSink.onMfaRequired = (state) => {
      if (mounted.current) setSession((prev) => (prev === null ? prev : { ...prev, mfa: state }));
    };
    void refresh();
    return () => {
      mounted.current = false;
      sessionSink.onUnauthorized = () => {};
      sessionSink.onMfaRequired = () => {};
    };
  }, [apply, refresh]);

  const mfa = session?.mfa ?? null;
  const value = useMemo<SessionState>(
    () => ({ session, status, mfa, markMfaVerified, login, loginWithPassword, logout, refresh }),
    [session, status, mfa, markMfaVerified, login, loginWithPassword, logout, refresh],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (value === null) {
    throw new Error("useSession must be used inside <SessionProvider>");
  }
  return value;
}
